import { useMemo, useRef, useState } from 'react';
import type { UserProfile } from '../../types';
import { dateUndatedBacklog } from '../../services/undatedBacklogService';
import {
    backlogActorName,
    backlogDatedNote,
    backlogSaveErrorText,
    chooseBacklogDate,
    nothingToDateNote,
    obviousRowsNote,
    selectAutoDatableRows,
    selectManualUndatedRows,
    selectUndatedRows,
    sumUndated,
    type BacklogRow,
} from './backlogDatingLogic';

/**
 * Nachtrag der Protokolluecke im Anlagedialog — Zustand und Ablauf.
 *
 * Aus InvoicesView herausgeloest. Hier stehen nur Zustand und die beiden
 * Schreibwege; die Regeln (Auswahl, Datum, Meldungen) liegen rein in
 * backlogDatingLogic, weil sie sich nur ohne React pruefen lassen.
 *
 * Generisch ueber die Zeile: die Ansicht reicht ihre Projektzeilen herein und
 * bekommt in manualUndatedRows dieselben Zeilen zurueck — ihre Liste liest an
 * ihnen weitere Felder (Projektnummer, Betraege der Luecke).
 */
export interface BacklogDatingInput<R extends BacklogRow> {
    /** Die Projektzeilen des Anlagedialogs. */
    projectRows: readonly R[];
    /** Letzter Rueckfall fuer das Datum (chooseBacklogDate). */
    today: string;
    /** Wer nachtraegt — sein Name steht danach im Protokoll. */
    userProfile: Pick<UserProfile, 'id' | 'first_name' | 'last_name'> | null;
    /**
     * Projektbestand nach dem Schreiben neu laden. Wirft nie: ein
     * fehlgeschlagenes Neuladen laesst die Liste stehen (refreshProjectList).
     */
    refreshProjects: () => Promise<void>;
}

export const useBacklogDating = <R extends BacklogRow>({
    projectRows,
    today,
    userProfile,
    refreshProjects,
}: BacklogDatingInput<R>) => {
    /**
     * Datum je Projekt, auf das dessen Mengen ohne Protokolleintrag
     * nachgetragen werden.
     *
     * Je Projekt und nicht gemeinsam: der richtige Tag ist der, an dem DIESES
     * Projekt seine Leistung erbracht hat. Ein Feld fuer alle waere derselbe
     * Fehlgriff wie die fruehere Vorbelegung mit dem Zeitraumende — ein Datum,
     * das fuer eines der Projekte stimmt und fuer die uebrigen nicht.
     *
     * Fehlt der Eintrag, gilt der Vorschlag der Zeile (backlogSuggestedDate).
     */
    const [backlogDates, setBacklogDates] = useState<Record<string, string>>({});
    /** Projekt, dessen Nachtrag gerade laeuft — sperrt nur dessen Schaltflaeche. */
    const [backlogBusy, setBacklogBusy] = useState<string | null>(null);
    /**
     * Laeuft die Automatik gerade?
     *
     * Ein Ref und KEIN Zustand. Als Zustand haette der Effekt an seinem
     * eigenen Ergebnis gehangen: setzen -> Neuzeichnen -> Effekt erneut ->
     * Aufraeumfunktion des vorigen Laufs -> der laufende Stapel bricht seine
     * Nachbereitung ab und die Sperre bliebe fuer immer stehen.
     */
    const autoRunningRef = useRef(false);
    const [backlogNote, setBacklogNote] = useState<string | null>(null);
    const [backlogError, setBacklogError] = useState<string | null>(null);
    /** Laeuft gerade der Stapel der eindeutigen Faelle? (handleDateObviousRows) */
    const [obviousBusy, setObviousBusy] = useState(false);

    /** Summe der undatierten offenen Mengen ueber die angezeigten Projekte. */
    const undatedTotal = useMemo(() => sumUndated(projectRows), [projectRows]);

    /** Das Datum, das der Nachtrag dieses Projekts tatsaechlich bekommt (chooseBacklogDate). */
    const backlogDateFor = (row: R): string => chooseBacklogDate(row, backlogDates, today);

    /** Projekte mit Mengen, die zu keinem Monat gehoeren. */
    const undatedRows = useMemo(() => selectUndatedRows(projectRows), [projectRows]);

    /**
     * Die eindeutig datierbaren — Regel und Begruendung (erster protokollierter
     * Tag, nachgerechnet an 6051) bei selectAutoDatableRows.
     */
    const autoDatableRows = useMemo(() => selectAutoDatableRows(undatedRows), [undatedRows]);

    /** Die uebrigen — hier entscheidet der Nutzer. */
    const manualUndatedRows = useMemo(
        () => selectManualUndatedRows(undatedRows, autoDatableRows),
        [undatedRows, autoDatableRows],
    );

    /**
     * Traegt die eindeutigen Faelle nach — auf Knopfdruck, nicht von selbst.
     *
     * FRUEHER lief das hier automatisch, sobald der Anlagedialog seinen
     * Bestand hatte, mit der Begruendung: an diesen Zeilen gibt es nichts zu
     * entscheiden. Die Begruendung stimmt, aber sie hat uebersehen, WER
     * danach im Protokoll steht. Der Nachtrag stempelt `performerName`, und
     * dieses Feld beantwortet die Frage, wer die Leistung ERBRACHT hat. So
     * trugen am 31.08.2026 acht Eintraege in drei Projekten den Namen einer
     * Kollegin, die nichts angeklickt hatte — sie hatte nur die Ansicht offen.
     *
     * Ein Vorgang, der ungefragt in fremde Projekte schreibt, gehoert
     * ausserdem nicht in eine Sitzung: er laeuft jetzt abends im Nachtlauf
     * (scripts/integrity-sweep.ts) und stempelt dort SYSTEM_ACTOR_NAME. Hier
     * bleibt der Knopf fuer den, der nicht bis zum Abend warten will — und
     * dann steht sein Name zu Recht darunter.
     *
     * NACHEINANDER und nicht parallel: jeder Nachtrag laedt sein Projekt,
     * prueft die Sperre und schreibt den ganzen Blob zurueck. Gleichzeitig
     * abgeschickt waeren das ein Dutzend Vollschreibvorgaenge auf einmal.
     *
     * Mit Deckel: mehr als BATCH pro Klick wird nicht angefasst. Der Rest
     * kommt beim naechsten Klick dran — das haelt einen Fehlgriff klein,
     * falls die Auswahlregel doch einmal zu weit greift.
     */
    const handleDateObviousRows = async () => {
        if (autoRunningRef.current) return;
        const BATCH = 20;
        const todo = autoDatableRows.slice(0, BATCH);
        if (todo.length === 0) return;

        autoRunningRef.current = true;
        setObviousBusy(true);
        setBacklogError(null);
        setBacklogNote(null);
        let count = 0;
        let netCents = 0;
        const failed: string[] = [];
        try {
            for (const row of todo) {
                try {
                    const result = await dateUndatedBacklog(row.project.id, {
                        date: row.backlogSuggestedDate,
                        actorId: userProfile?.id ?? '',
                        actorName: backlogActorName(userProfile),
                    });
                    if (result.count > 0) {
                        count += result.count;
                        netCents += result.netCents;
                    }
                } catch {
                    // Fremde Projektsperre oder Schreibfehler. Die Zeile steht
                    // danach weiter in der Liste und laesst sich einzeln
                    // datieren; der Name kommt unten in die Meldung.
                    failed.push(row.project.name);
                }
            }
            if (count > 0) {
                await refreshProjects();
            }
            setBacklogNote(obviousRowsNote(count, netCents, failed));
        } finally {
            autoRunningRef.current = false;
            setObviousBusy(false);
        }
    };

    /**
     * Traegt die Protokolluecke eines Projekts nach und laedt den Bestand neu.
     *
     * WARUM ueberhaupt: der Haken darueber wirkt nur auf diesen einen Beleg.
     * Die Menge bleibt undatiert, faellt beim naechsten Zeitraum wieder heraus
     * und blockiert kumulativ jeden Beleg, dessen Grundlage dadurch unter die
     * Summe der Vorbelege rutscht (advance_exceeds_rate_basis,
     * payable_negative). Hier wird die Ursache geschlossen.
     *
     * Neu geladen wird der Projektbestand, nicht der Belegbestand: geaendert
     * hat sich das Leistungsprotokoll, nicht die Rechnungslage. Ohne das
     * Verwerfen des Zwischenspeichers kaeme derselbe Stand von vorhin zurueck
     * und der gelbe Kasten stuende weiter da, obwohl die Luecke geschlossen
     * ist.
     */
    const handleDateBacklog = async (row: R) => {
        setBacklogError(null);
        setBacklogNote(null);
        setBacklogBusy(row.project.id);
        try {
            const date = backlogDateFor(row);
            const result = await dateUndatedBacklog(row.project.id, {
                date,
                actorId: userProfile?.id ?? '',
                actorName: backlogActorName(userProfile),
            });
            if (result.count === 0) {
                setBacklogNote(nothingToDateNote(row.project.name));
                return;
            }
            // Ab hier ist gespeichert. Das Neuladen darf deshalb NICHT im catch
            // darunter landen: der meldet "Der Nachtrag konnte nicht gespeichert
            // werden", und nach einem bloss fehlgeschlagenen Neuladen waere das
            // falsch — der Nachtrag steht ja im Protokoll. refreshProjects wirft
            // nie; schlaegt das Laden fehl, bleibt die Liste auf dem alten Stand
            // und die Erfolgsmeldung kommt trotzdem.
            await refreshProjects();
            setBacklogNote(backlogDatedNote(row.project.name, result.count, result.netCents, date));
        } catch (e: unknown) {
            setBacklogError(backlogSaveErrorText(e));
        } finally {
            setBacklogBusy(null);
        }
    };

    return {
        setBacklogDates,
        backlogBusy,
        backlogNote,
        backlogError,
        obviousBusy,
        undatedTotal,
        backlogDateFor,
        autoDatableRows,
        manualUndatedRows,
        handleDateObviousRows,
        handleDateBacklog,
    };
};
